import type { VariableType } from "./types";
import { VARIABLE_TYPES } from "./types";
import { type GenericSchema, type InferOutput, instance, object, picklist } from "valibot";
import VariableId from "./VariableId";
import type { IconName } from "../components/icon/Icon";
import type { RuleConditionBehaviour, VariableConfig } from "../fields/types";
import type { TranslationKey } from "../state/i18n";
import { useTranslation } from "../state/i18n";
import type { Setter, VoidComponent } from "solid-js";
import { createEffect, createMemo, createSignal, onCleanup, Show, untrack } from "solid-js";
import VariableService from "../services/VariableService";
import { MAX_NUMBER_XL, MAX_NUMBER_XL_NEGATIVE } from "../constants";
import { isEmpty, isNil, uniq } from "lodash-es";
import { TextInput } from "../components/inputs/TextInput";
import { Dynamic } from "solid-js/web";
import { NumberInput } from "../components/inputs/NumberInput";
import { DateInput, DateTimeInput } from "../components/inputs/DateInput";
import { useFormServices } from "../services/formServices";
import { dataName, label } from "../utils/validation/base";
import { SingleSelectInput } from "../components/menu/Select";
import { VariablePropertyContainer } from "../components/field/propertyContainer/PropertyContainer";
import type { TFunction } from "i18next";
import { keyFromSelector } from "i18next";
import NavigationService from "../services/NavigationService";
import RuleService from "../services/RuleService";
import FieldId from "../fields/FieldId";
import { getDynamicValueIcon } from "../utils/menu/optionIconUtil";
import VariableShowEvent from "../events/VariableShowEvent";
import type { SelectOption } from "../types/Menu";
import { makeEventListener } from "@solid-primitives/event-listener";
import IntegrationId from "../integrations/IntegrationId";
import { integrationsIcons } from "../integrations/IntegrationIcons";
import { MenuPill } from "../components/pill/InteractivePill";
import PillSingleSelectTrigger from "../components/menu/triggers/PillSingleSelectTrigger";
import RuleId from "../fields/RuleId";

export const VariableBaseSchema = object({
  uid: instance(VariableId),
  name: label(),
  dataName: dataName(),
  type: picklist(VARIABLE_TYPES),
});

export type BaseVariableType = InferOutput<typeof VariableBaseSchema> & {
  defaultValue: unknown;
};

type Optional<T, K extends keyof T> = Pick<Partial<T>, K> & Omit<T, K>;

type DefaultValues = Pick<BaseVariableDefinition, "ListItem" | "propertyKeys">;

type UntranslatedKeys = keyof Pick<BaseVariableType, "uid" | "type">;

export const valueKinds = {
  Field: "field",
  Integration: "integration",
  Placeholder: "placeholder",
} as const;

export type SelectOptionValueKind = (typeof valueKinds)[keyof typeof valueKinds];

export type BaseVariableDefinition<T extends BaseVariableType = BaseVariableType> = {
  type: VariableType;
  schema: GenericSchema<T>;
  icon: IconName;
  ruleConditionBehaviour: RuleConditionBehaviour;
  titleKey: TranslationKey;
  propertyKeys: Record<keyof Omit<T, UntranslatedKeys>, TranslationKey>;
  ListItem: VoidComponent<ListItemProps>;
};

export type VariableDefinition<T extends BaseVariableType = BaseVariableType> = Optional<
  BaseVariableDefinition<T>,
  keyof DefaultValues
>;

export const initVariable = (variable: VariableDefinition): BaseVariableDefinition => ({
  ...createDefaultValues(variable),
  ...variable,
});

export type ListItemProps = { uid: VariableId; onDelete: (id: VariableId) => void; disabled?: boolean };

const createDefaultListItem =
  (variable: VariableDefinition): VoidComponent<ListItemProps> =>
  (props) => {
    const { t } = useTranslation();
    const { validationService } = useFormServices();
    const config = VariableService.getVariable(untrack(() => props.uid));
    const usedBy = () => uniq(VariableService.usedBy(props.uid));
    const usedByLabel = createMemo(() =>
      isEmpty(usedBy()) ? t(($) => $.USED_BY_NONE) : t(($) => $.USED_BY, { count: usedBy().length }),
    );
    const [open, setOpen] = createSignal(config?.defaultValue === undefined || false);
    const issues = createMemo(() => validationService.getIssuesForVariable(untrack(() => props.uid)));
    const id = untrack(() => props.uid);

    if (isNil(config)) {
      throw new Error("Variable does not exist: " + untrack(() => props.uid));
    }

    addNavigationListener(
      untrack(() => props.uid),
      setOpen,
    );

    const getErrorMessage = (property: keyof VariableConfig) =>
      issues().find((issue) => issue.path?.some((path) => path.key === property) ?? false)?.message;

    const Content = () => (
      <div class="flex flex-col gap-6">
        <TextInput
          value={config.name}
          onBlur={(e) => {
            VariableService.setName(config?.uid, e.target.value);
          }}
          label={t(($) => $.VARIABLE_PROPERTY_NAME)}
          errorMessage={getErrorMessage("name")}
          required
          disabled={props.disabled}
        />
        <Dynamic
          id={props.uid}
          value={config.defaultValue}
          errorMessage={getErrorMessage("defaultValue")}
          component={defaultValueInputOptions[variable.type]}
          t={t}
          disabled={props.disabled}
        />
      </div>
    );

    const pillMenuOptions = createMemo<SelectOption<FieldId | RuleId | IntegrationId>[]>(() =>
      usedBy().map((item): SelectOption<FieldId | RuleId | IntegrationId> => {
        if (item.kind === "field") {
          const icon = getDynamicValueIcon(item.field.uid, item.field.type);

          return isNil(icon)
            ? {
                value: item.field.uid,
                label: item.field.properties.label_text as string,
                variant: "text",
              }
            : {
                value: item.field.uid,
                label: item.field.properties.label_text as string,
                variant: "dynamicValue",
                icon: {
                  name: icon.name,
                  class: icon.class,
                },
                pill: item.field.properties.data_name as string,
              };
        } else if (item.kind === "rule") {
          const index = RuleService.getAll().findIndex((rule) => rule.id.equals(item.rule.id));

          return {
            variant: "icon",
            icon: {
              name: "rulesIcon",
              type: "solid",
              class: "icon-primary",
            },
            value: item.rule.id,
            label: item.rule.name ?? t(($) => $.RULES_MENU_EMPTY_RULE_NAME, { index: index + 1 }),
          };
        } else {
          return {
            variant: "icon",
            icon: {
              name: integrationsIcons[item.integration.type],
              type: "solid",
              class: "icon-primary",
            },
            value: item.integration.id,
            label: item.integration.name,
          };
        }
      }),
    );

    createEffect(() => {
      makeEventListener<Record<string, VariableShowEvent>>(document, `variable.show-${id.toString()}`, (event) => {
        document.getElementById(event.variableId.toString())?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    });

    return (
      <article aria-label={config.name ?? config.dataName} class="relative" id={id.toString()}>
        <VariablePropertyContainer
          typeLabel={config.type}
          open={open()}
          setOpen={() => setOpen(!open())}
          Content={() => Content()}
          error={!isEmpty(issues())}
          pillMenuComponent={() => (
            <Show when={!open()}>
              <MenuPill
                label={usedByLabel()}
                icon="link"
                Trigger={(arkProps) => (
                  <PillSingleSelectTrigger
                    arkProps={arkProps()}
                    value={{
                      variant: "icon",
                      icon: { name: "link", type: "solid" },
                      label: usedByLabel(),
                      value: id,
                    }}
                    disabled={isEmpty(usedBy())}
                  />
                )}
                onSelect={(optionId) => {
                  if (optionId instanceof FieldId) {
                    NavigationService.navigateToField(optionId, "instant");
                  } else if (optionId instanceof RuleId) {
                    NavigationService.navigateToRule(optionId);
                  } else if (optionId instanceof IntegrationId) {
                    NavigationService.navigateToIntegration(optionId);
                  }
                }}
                items={pillMenuOptions()}
              />
            </Show>
          )}
          label={config.name}
          icon={variable.icon}
          dataName={config.dataName}
          dataNameError={getErrorMessage("dataName")}
          setDataName={(name) => VariableService.setDataName(props.uid, name, undefined, true)}
          deleteItem={() => props.onDelete(props.uid)}
          disabled={props.disabled}
        />
      </article>
    );
  };

type DefaultValueInput<T = unknown> = VoidComponent<{
  id: VariableId;
  value: T;
  errorMessage?: string;
  t: TFunction;
  disabled?: boolean;
}>;

const DefaultValueStringInput: DefaultValueInput<string> = (props) => (
  <TextInput
    value={props.value}
    onBlur={(e) => {
      VariableService.setDefaultValue(props.id, e.target.value);
      new VariableShowEvent(props.id).send();
    }}
    label={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    errorMessage={props.errorMessage}
    required
    disabled={props.disabled}
  />
);

const DefaultValueNumberInput: DefaultValueInput<number> = (props) => (
  <NumberInput
    value={props.value}
    onBlur={(value) => {
      VariableService.setDefaultValue(props.id, value ?? 0);
      new VariableShowEvent(props.id).send();
    }}
    minValue={MAX_NUMBER_XL_NEGATIVE}
    maxValue={MAX_NUMBER_XL}
    allowDecimal
    label={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    errorMessage={props.errorMessage}
    required
    disabled={props.disabled}
  />
);

const DefaultValueBooleanInput: DefaultValueInput<boolean> = (props) => (
  <SingleSelectInput
    value={props.value}
    onSelect={(value) => {
      VariableService.setDefaultValue(props.id, value);
      new VariableShowEvent(props.id).send();
    }}
    options={[
      { variant: "text", value: true, label: "True" },
      { variant: "text", value: false, label: "False" },
    ]}
    label={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    errorMessage={props.errorMessage}
    sameWidth
    required
    disabled={props.disabled}
  />
);

const DefaultValueDateInput: DefaultValueInput<string> = (props) => (
  <DateInput
    ariaLabel={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    value={props.value}
    onBlur={(e) => {
      VariableService.setDefaultValue(props.id, e.target.value);
      new VariableShowEvent(props.id).send();
    }}
    label={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    errorMessage={props.errorMessage}
    required
    disabled={props.disabled}
  />
);

const DefaultValueDateTimeInput: DefaultValueInput<string> = (props) => (
  <DateTimeInput
    ariaLabel={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    value={props.value}
    onBlur={(e) => {
      VariableService.setDefaultValue(props.id, e.target.value);
      new VariableShowEvent(props.id).send();
    }}
    label={props.t(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE)}
    errorMessage={props.errorMessage}
    required
    disabled={props.disabled}
  />
);

const addNavigationListener = (id: VariableId, setOpen: Setter<boolean>) => {
  const openEventHandler = () => {
    document.getElementById(id.toString())?.scrollIntoView({ behavior: "smooth", block: "center" });
    setOpen(true);
  };

  createEffect(() => {
    const eventName = `variable.show-${id.toString()}`;
    document.addEventListener(eventName, openEventHandler);

    onCleanup(() => {
      document.removeEventListener(eventName, openEventHandler);
    });
  });
};

const defaultValueInputOptions: Record<VariableType, DefaultValueInput> = {
  string: DefaultValueStringInput as DefaultValueInput,
  number: DefaultValueNumberInput as DefaultValueInput,
  boolean: DefaultValueBooleanInput as DefaultValueInput,
  date: DefaultValueDateInput as DefaultValueInput,
  datetime: DefaultValueDateTimeInput as DefaultValueInput,
};

const createDefaultPropertyKeys = (): Record<keyof Omit<BaseVariableType, UntranslatedKeys>, TranslationKey> => ({
  name: keyFromSelector(($) => $.VARIABLE_PROPERTY_NAME),
  dataName: keyFromSelector(($) => $.VARIABLE_PROPERTY_DATA_NAME),
  defaultValue: keyFromSelector(($) => $.VARIABLE_PROPERTY_DEFAULT_VALUE),
});

const createDefaultValues = <T extends BaseVariableType = BaseVariableType>(
  variable: VariableDefinition<T>,
): DefaultValues => ({
  ListItem: createDefaultListItem(variable),
  propertyKeys: createDefaultPropertyKeys(),
});
